import { CATEGORY_PURPOSE } from '@pet/shared';
import type { PaymentDetail, PaymentSource, SpendingPurpose } from '@pet/shared';
import { formatCentsAsDollars, parseDollarsToCents } from './money';

// "Fix this payment" (AD-19): the saved payment's answers as a wizard draft. Pure, no network.
// Funding source and owner are never part of it (ux-wizard.md: "never preselected"); the old
// source is kept only so the prefilled method survives tapping that source again.

export interface FixLine {
  propertyId: string;
  taxCategoryKey: string;
  amountCents: number;
  description: string;
  // The stored value, which may differ from the category's default. Set after the category.
  deMinimisEligible: boolean;
  // '' when the category is offered under no answer (depreciation, or an unknown key).
  purpose: SpendingPurpose | '';
}

export interface FixDraft {
  payee: string;
  date: string; // YYYY-MM-DD
  paymentMethod: string;
  oldSource: PaymentSource;
  totalText: string;
  // A property id, or 'split'.
  where: string;
  lines: FixLine[];
  personalText: string;
  // For the confirm's sentence: a note or files that the new entry will not carry.
  memoLost: boolean;
  otherAttachments: number;
  // A RECEIPT attachment on the old payment (it comes back only through a reopened ingestion).
  hadReceipt: boolean;
}

// composeMemo's form exactly (allocation.ts); formatCentsAsDollars writes no grouping commas.
const COMPOSED_MEMO = /^Not for the properties: \$([0-9]+\.[0-9]{2}) of \$([0-9]+\.[0-9]{2}) total$/;

// The personal part, only when the memo is the wizard's own and agrees with the lines.
function personalPart(memo: string | null, linesCents: number): { personalCents: number; totalCents: number } | null {
  const match = memo === null ? null : COMPOSED_MEMO.exec(memo);
  if (!match) return null;
  const personalCents = parseDollarsToCents(match[1]);
  const totalCents = parseDollarsToCents(match[2]);
  if (personalCents === null || totalCents === null || personalCents <= 0) return null;
  if (totalCents !== linesCents + personalCents) return null;
  return { personalCents, totalCents };
}

function purposeOf(key: string): SpendingPurpose | '' {
  const purpose = (CATEGORY_PURPOSE as Record<string, string | undefined>)[key];
  return purpose === 'improvement' || purpose === 'repair_or_upkeep' || purpose === 'not_building_work'
    ? purpose
    : '';
}

export function fixDraftFrom(payment: PaymentDetail): FixDraft {
  const lines: FixLine[] = payment.lines.map((l) => ({
    propertyId: l.propertyId,
    taxCategoryKey: l.taxCategoryKey,
    amountCents: l.amountCents,
    description: l.description ?? '',
    deMinimisEligible: l.deMinimisEligible,
    purpose: purposeOf(l.taxCategoryKey),
  }));
  const linesCents = lines.reduce((sum, l) => sum + l.amountCents, 0);
  const personal = personalPart(payment.memo, linesCents);
  // The one-property path writes the whole total to its single line and has no personal part,
  // so a payment with one is reopened on Split to keep its line amount and its memo.
  const where = lines.length === 1 && personal === null ? lines[0].propertyId : 'split';

  return {
    payee: payment.payee,
    date: payment.date.slice(0, 10),
    paymentMethod: payment.paymentMethod ?? '',
    oldSource: payment.paymentSource,
    totalText: formatCentsAsDollars(personal ? personal.totalCents : linesCents),
    where,
    lines,
    personalText: personal ? formatCentsAsDollars(personal.personalCents) : '',
    memoLost: (payment.memo?.trim() ?? '') !== '' && personal === null,
    otherAttachments: payment.attachments.filter((a) => a.kind !== 'RECEIPT').length,
    hadReceipt: payment.attachments.some((a) => a.kind === 'RECEIPT'),
  };
}
